import React, { useEffect, useRef } from 'react';

// Animated "grid" backdrop for NetLab: buses (circles) and assets (boxes)
// wired together by lines, with pulses of current running along them.
// A pulse drags a glowing tail, bends through the nodes it visits and
// lights each node up as it passes. Hovering brightens nearby assets and
// clicking injects a burst of current from the closest node.

const CELL = 92;              // jittered-grid spacing between nodes (px)
const OCCUPANCY = 0.78;       // share of grid cells that get a node
const TRAIL = 120;            // tail length of a pulse (px)
const PULSE_DENSITY = 1 / 32000; // pulses per px² of screen
const MAX_PULSES = 70;
const HUES = [268, 280, 292, 256, 305]; // purple → violet → magenta

function makeGlowSprite(size, rgb) {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, `rgba(${rgb},1)`);
  grad.addColorStop(0.25, `rgba(${rgb},0.45)`);
  grad.addColorStop(1, `rgba(${rgb},0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  return c;
}

function buildGrid(w, h) {
  const cols = Math.ceil(w / CELL) + 2;
  const rows = Math.ceil(h / CELL) + 2;
  const cells = [];
  const nodes = [];

  for (let r = 0; r < rows; r++) {
    cells.push([]);
    for (let c = 0; c < cols; c++) {
      if (Math.random() > OCCUPANCY) { cells[r].push(null); continue; }
      const roll = Math.random();
      const kind = roll < 0.58 ? 'bus' : roll < 0.9 ? 'asset' : 'substation';
      const node = {
        x: (c - 1) * CELL + (Math.random() - 0.5) * CELL * 0.62,
        y: (r - 1) * CELL + (Math.random() - 0.5) * CELL * 0.62,
        kind,
        size: kind === 'bus' ? 2.6 + Math.random() * 1.6
          : kind === 'asset' ? 4.5 + Math.random() * 2
          : 8 + Math.random() * 2,
        energy: 0,
        hue: 272,
        edges: [],
      };
      node.id = nodes.length;
      nodes.push(node);
      cells[r].push(node);
    }
  }

  const edges = [];
  const link = (a, b) => {
    if (!a || !b) return;
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const edge = { a, b, len, energy: 0 };
    edges.push(edge);
    a.edges.push(edge);
    b.edges.push(edge);
  };

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const n = cells[r][c];
      if (!n) continue;
      const right = cells[r][c + 1] || (Math.random() < 0.5 ? cells[r][c + 2] : null);
      const down = cells[r + 1] && (cells[r + 1][c] || (Math.random() < 0.5 && cells[r + 2] ? cells[r + 2][c] : null));
      if (Math.random() < 0.86) link(n, right);
      if (Math.random() < 0.86) link(n, down);
      // One diagonal per cell pair at most, so lines rarely cross.
      if (cells[r + 1] && Math.random() < 0.28) {
        if ((r + c) % 2 === 0) link(n, cells[r + 1][c + 1]);
        else if (c > 0) link(n, cells[r + 1][c - 1]);
      }
    }
  }

  return { nodes: nodes.filter(n => n.edges.length > 0), edges };
}

function otherEnd(edge, node) {
  return edge.a === node ? edge.b : edge.a;
}

export default function NetLabBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const glow = makeGlowSprite(64, '205,170,255');
    const headGlow = makeGlowSprite(64, '240,225,255');

    let w = 0, h = 0, dpr = 1;
    let grid = { nodes: [], edges: [] };
    let pulses = [];
    let raf = 0;
    let last = performance.now();
    const mouse = { x: -9999, y: -9999 };

    const spawnPulse = (from, hue) => {
      if (!from || from.edges.length === 0) return null;
      const edge = from.edges[(Math.random() * from.edges.length) | 0];
      return {
        edge,
        from,
        t: 0,
        speed: 110 + Math.random() * 140,
        hue: hue ?? HUES[(Math.random() * HUES.length) | 0],
        trail: [{ x: from.x, y: from.y }],
        life: 12 + Math.random() * 18,
      };
    };

    const randomNode = () => grid.nodes[(Math.random() * grid.nodes.length) | 0];

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const box = (canvas.parentElement || canvas).getBoundingClientRect();
      w = Math.max(1, Math.round(box.width));
      h = Math.max(1, Math.round(box.height));
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      grid = buildGrid(w, h);
      const target = Math.min(MAX_PULSES, Math.round(w * h * PULSE_DENSITY));
      pulses = [];
      for (let i = 0; i < target; i++) {
        const p = spawnPulse(randomNode());
        if (p) { p.t = Math.random(); pulses.push(p); }
      }
    };

    const advance = (p, dt) => {
      let dist = p.speed * dt;
      while (dist > 0) {
        const remaining = (1 - p.t) * p.edge.len;
        if (dist < remaining) {
          p.t += dist / p.edge.len;
          dist = 0;
        } else {
          dist -= remaining;
          const to = otherEnd(p.edge, p.from);
          p.trail.push({ x: to.x, y: to.y });
          to.energy = 1;
          to.hue = p.hue;
          p.edge.energy = 1;
          // Keep moving forward: avoid bouncing straight back if we can.
          const options = to.edges.filter(e => e !== p.edge);
          const pool = options.length ? options : to.edges;
          p.edge = pool[(Math.random() * pool.length) | 0];
          p.from = to;
          p.t = 0;
        }
      }
      const to = otherEnd(p.edge, p.from);
      p.x = p.from.x + (to.x - p.from.x) * p.t;
      p.y = p.from.y + (to.y - p.from.y) * p.t;
      p.edge.energy = Math.max(p.edge.energy, 0.6);
      p.edgeHue = p.hue;

      // Trim the tail to TRAIL px, measured back from the head.
      let acc = 0;
      let px = p.x, py = p.y;
      for (let i = p.trail.length - 1; i >= 0; i--) {
        const q = p.trail[i];
        acc += Math.hypot(px - q.x, py - q.y);
        if (acc > TRAIL) { p.trail.splice(0, i); break; }
        px = q.x; py = q.y;
      }
    };

    const drawNode = (n, extra) => {
      const e = Math.min(1, n.energy + extra);
      const light = 42 + e * 36;
      const alpha = 0.45 + e * 0.55;
      ctx.fillStyle = `hsla(${n.hue},70%,${light}%,${alpha})`;
      ctx.strokeStyle = `hsla(${n.hue},85%,${Math.min(95, light + 18)}%,${0.55 + e * 0.45})`;
      ctx.lineWidth = 1.2;
      const s = n.size * (1 + e * 0.35);
      if (n.kind === 'bus') {
        ctx.beginPath();
        ctx.arc(n.x, n.y, s, 0, Math.PI * 2);
        ctx.fill();
      } else if (n.kind === 'asset') {
        ctx.fillRect(n.x - s, n.y - s, s * 2, s * 2);
        ctx.strokeRect(n.x - s, n.y - s, s * 2, s * 2);
      } else {
        // Substation: hollow box with a bus dot inside.
        ctx.strokeRect(n.x - s, n.y - s, s * 2, s * 2);
        ctx.beginPath();
        ctx.arc(n.x, n.y, s * 0.4, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const frame = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      const bg = ctx.createRadialGradient(w * 0.5, h * 0.42, 0, w * 0.5, h * 0.5, Math.max(w, h) * 0.75);
      bg.addColorStop(0, '#1f1140');
      bg.addColorStop(0.55, '#120a2a');
      bg.addColorStop(1, '#07040f');
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Wires
      ctx.lineCap = 'round';
      for (const e of grid.edges) {
        e.energy *= Math.pow(0.18, dt);
        ctx.strokeStyle = `rgba(${150 + e.energy * 80},${118 + e.energy * 70},${220 + e.energy * 35},${0.13 + e.energy * 0.18})`;
        ctx.lineWidth = 1 + e.energy * 0.4;
        ctx.beginPath();
        ctx.moveTo(e.a.x, e.a.y);
        ctx.lineTo(e.b.x, e.b.y);
        ctx.stroke();
      }

      // Pulses
      ctx.globalCompositeOperation = 'lighter';
      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i];
        if (!reduceMotion) advance(p, dt);
        else if (p.x === undefined) advance(p, 0);
        p.life -= dt;

        const pts = [...p.trail, { x: p.x, y: p.y }];
        const n = pts.length;
        for (let k = 1; k < n; k++) {
          const f = k / (n - 1);
          ctx.strokeStyle = `hsla(${p.hue},85%,${55 + f * 20}%,${f * 0.55})`;
          ctx.lineWidth = 0.5 + f * 1.3;
          ctx.beginPath();
          ctx.moveTo(pts[k - 1].x, pts[k - 1].y);
          ctx.lineTo(pts[k].x, pts[k].y);
          ctx.stroke();
        }
        ctx.globalAlpha = 0.6;
        ctx.drawImage(glow, p.x - 11, p.y - 11, 22, 22);
        ctx.globalAlpha = 0.8;
        ctx.drawImage(headGlow, p.x - 4, p.y - 4, 8, 8);
        ctx.globalAlpha = 1;

        if (p.life <= 0 && !reduceMotion) {
          pulses.splice(i, 1);
          const np = spawnPulse(randomNode());
          if (np) pulses.push(np);
        }
      }

      // Nodes (glow halo first, then the shape on top)
      for (const node of grid.nodes) {
        node.energy *= Math.pow(0.22, dt);
        const d = Math.hypot(node.x - mouse.x, node.y - mouse.y);
        const hover = d < 160 ? (1 - d / 160) * 0.6 : 0;
        const e = Math.min(1, node.energy + hover);
        if (e > 0.04) {
          const r = (node.size + 6) * (1 + e * 1.4);
          ctx.globalAlpha = e * 0.6;
          ctx.drawImage(glow, node.x - r, node.y - r, r * 2, r * 2);
          ctx.globalAlpha = 1;
        }
        node._hover = hover;
      }
      ctx.globalCompositeOperation = 'source-over';
      for (const node of grid.nodes) drawNode(node, node._hover);

      if (!reduceMotion) raf = requestAnimationFrame(frame);
    };

    const toLocal = (e) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const onMove = (e) => { const p = toLocal(e); mouse.x = p.x; mouse.y = p.y; };
    const onLeave = () => { mouse.x = -9999; mouse.y = -9999; };
    const onClick = (e) => {
      const at = toLocal(e);
      let best = null, bestD = Infinity;
      for (const n of grid.nodes) {
        const d = Math.hypot(n.x - at.x, n.y - at.y);
        if (d < bestD) { bestD = d; best = n; }
      }
      if (!best || bestD > 200) return;
      best.energy = 1;
      const hue = HUES[(Math.random() * HUES.length) | 0];
      for (let i = 0; i < 6; i++) {
        const p = spawnPulse(best, hue);
        if (p) { p.speed *= 1.4; p.life = 4 + Math.random() * 3; pulses.push(p); }
      }
      if (pulses.length > MAX_PULSES * 1.6) pulses.splice(0, pulses.length - MAX_PULSES * 1.6);
    };

    let resizeTimer = 0;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        resize();
        if (reduceMotion) frame(performance.now());
      }, 150);
    };

    resize();
    raf = requestAnimationFrame(frame);
    window.addEventListener('resize', onResize);
    window.addEventListener('mousemove', onMove);
    document.documentElement.addEventListener('mouseleave', onLeave);
    window.addEventListener('click', onClick);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMove);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('click', onClick);
    };
  }, []);

  return <canvas ref={canvasRef} className="netlab-background" aria-hidden="true" />;
}
